/**
 * Lead-room + info-dealer state (Sprint 3a). Pure data only.
 * Holds each fake lead room's live call, a small resolved-call history the
 * player uses to learn the room's reliability, VIP unlock timers, and the
 * one-shot hints bought from the info dealer for game money.
 */
import { LEAD_ROOMS, LEAD_ROOM_CONFIG } from './config/leadRooms.js';
import { CHATTER_CONFIG } from './config/roomChatter.js';
import type { ChatterCategory, ReactionMood } from './config/roomChatter.js';
import type { InfoProductId } from './config/infoShop.js';
import { migrateBags, type BagStore } from './shuffleBag.js';

export type CallStatus = 'live' | 'hit' | 'miss';

/** A room master's active or historical call. */
export interface RoomCall {
  /** unique id (roomId + createdAt) */
  id: string;
  coinId: string;
  /** the direction the master TOLD you to expect */
  claimedDirection: 1 | -1;
  /** phrasing variant 1..callVariants */
  variant: number;
  createdAt: number;
  resolveAt: number;
  status: CallStatus;
  /** set on resolve: which way the price actually went */
  actualDirection?: 1 | -1;
  /** set on resolve: was it a hidden dump trap (collusion) */
  trap?: boolean;
}

/** One line of live NPC banter (or the player's own reaction). */
export interface ChatMsg {
  id: string;
  /** index into NPC_NICKS; -1 = the player */
  nickIdx: number;
  /** banter category (drives the i18n pool); 'echo' for NPC reaction replies */
  category: ChatterCategory | 'echo';
  /** index into the category's i18n pool */
  textIdx: number;
  /** for echo lines: which mood pool (up/down/neut) */
  mood?: ReactionMood;
  /** for the player's own reaction lines: the reaction key to render verbatim */
  reactionKey?: string;
  at: number;
}

/** A rumor floating in the chat with a HIDDEN truth the player can fact-check. */
export interface Rumor {
  id: string;
  /** i18n key = 'rumor_' + templateId + '_' + variant (variant absent = legacy _1) */
  templateId: string;
  /** phrasing variant 1..N within the kind (defaults to 1 for legacy saves) */
  variant?: number;
  coinId: string;
  direction: 1 | -1;
  /** hidden until fact-checked or self-resolved */
  genuine: boolean;
  createdAt: number;
  /** when the rumor auto-resolves (verdict shown even without a fact check) */
  resolveAt: number;
  /** true once the player paid an ad to reveal the verdict early */
  factChecked: boolean;
  /** true once genuine rumors have reserved their real price move */
  fired: boolean;
}

export interface RoomRuntime {
  activeCall: RoomCall | null;
  /** newest-first resolved calls, max maxHistory */
  history: RoomCall[];
  nextCallAt: number;
  /** VIP rooms: unlocked (ad watched) until this time */
  unlockedUntil: number;
  /** newest-last live chatter ring buffer (max maxChat) */
  chat: ChatMsg[];
  /** active/unresolved rumors (max maxRumors) */
  rumors: Rumor[];
  /** when the next chatter line is emitted */
  nextChatAt: number;
}

/** A one-shot hint bought from the info dealer (consumed on view / TTL). */
export interface InfoHint {
  productId: InfoProductId;
  /** hint payload varies by product (coinId, roomId, or band) */
  coinId?: string;
  roomId?: string;
  band?: 'low' | 'mid' | 'high';
  boughtAt: number;
  expiresAt: number;
}

export interface LeadRoomState {
  rooms: Record<string, RoomRuntime>;
  /** active info-dealer hints (trimmed by TTL) */
  hints: InfoHint[];
  /**
   * Shuffle-bag cursors for lead-room text pools (chat banter per room+category,
   * rumor kind + per-kind variant, NPC echo pools). Only { seed, cursor } saved.
   */
  bags: BagStore;
}

function randRange(min: number, max: number): number {
  return min + Math.random() * (max - min);
}

function freshRoom(vip: boolean): RoomRuntime {
  const now = Date.now();
  return {
    activeCall: null,
    history: [],
    nextCallAt: now + randRange(
      LEAD_ROOM_CONFIG.firstCallDelayMin,
      LEAD_ROOM_CONFIG.firstCallDelayMax
    ),
    unlockedUntil: vip ? 0 : Number.MAX_SAFE_INTEGER, // free rooms always "unlocked"
    chat: [],
    rumors: [],
    nextChatAt: now + randRange(CHATTER_CONFIG.emitIntervalMin, CHATTER_CONFIG.emitIntervalMax),
  };
}

export function createInitialLeadRoomState(): LeadRoomState {
  const rooms: Record<string, RoomRuntime> = {};
  for (const cfg of LEAD_ROOMS) rooms[cfg.id] = freshRoom(cfg.vip);
  return { rooms, hints: [], bags: {} };
}

/** Migrate a loaded save: keep known rooms' runtime, add missing rooms/fields. */
export function migrateLeadRoomState(saved: Partial<LeadRoomState> | undefined): LeadRoomState {
  const fresh = createInitialLeadRoomState();
  if (!saved || typeof saved !== 'object') return fresh;

  const rooms: Record<string, RoomRuntime> = {};
  for (const cfg of LEAD_ROOMS) {
    const s = saved.rooms?.[cfg.id];
    const base = fresh.rooms[cfg.id];
    rooms[cfg.id] = s && typeof s === 'object'
      ? {
          activeCall: s.activeCall ?? null,
          history: Array.isArray(s.history) ? s.history.slice(0, LEAD_ROOM_CONFIG.maxHistory) : [],
          nextCallAt: typeof s.nextCallAt === 'number' ? s.nextCallAt : base.nextCallAt,
          unlockedUntil: typeof s.unlockedUntil === 'number'
            ? (cfg.vip ? s.unlockedUntil : Number.MAX_SAFE_INTEGER)
            : base.unlockedUntil,
          // new fields default when absent (backward compatible)
          chat: Array.isArray(s.chat) ? s.chat.slice(-CHATTER_CONFIG.maxChat) : base.chat,
          rumors: Array.isArray(s.rumors) ? s.rumors.slice(0, CHATTER_CONFIG.maxRumors) : base.rumors,
          nextChatAt: typeof s.nextChatAt === 'number' ? s.nextChatAt : base.nextChatAt,
        }
      : base;
  }
  const hints = Array.isArray(saved.hints) ? saved.hints : [];
  return { rooms, hints, bags: migrateBags(saved.bags) };
}
